import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Ban, Download, Play, RefreshCw, RotateCcw, Undo2 } from 'lucide-react';

import {
  ACTION_LABELS,
  type AssetJob,
  useJobControlMutation,
  useJobItemsQuery,
  useJobQuery,
  useJobsQuery,
} from '@/client/api/assets/jobs';
import { useWorkerChangeMutation, useWorkersScheduleQuery } from '@/client/api/workers/workers.query';
import JobLauncherDialog from '@/client/components/admin/Assets/JobLauncherDialog';
import Pagination from '@/client/components/common/Pagination';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { getApiErrorMessage } from '@/client/utils/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

const STATUS_CLS: Record<string, string> = {
  queued: 'bg-gray-100 text-gray-700', running: 'bg-blue-100 text-blue-700', done: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700', cancelled: 'bg-amber-100 text-amber-800',
};
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');
const describe = (j: AssetJob) =>
  j.kind === 'signal'
    ? `Analysis: ${((j.params?.processors as string[]) ?? ['all']).map((p) => p.replace('task:asset-', '')).join(', ')} (${j.params?.mode ?? 'missing'})`
    : j.kind === 'revert' ? `Revert of job #${j.reverts_ajid} (${ACTION_LABELS[j.action] ?? j.action})`
      : `${ACTION_LABELS[j.action] ?? j.action}${j.params?.reason_code ? ` · ${j.params.reason_code}` : ''}`;

const Progress = ({ j }: { j: AssetJob }) => {
  const pct = (n: number) => `${j.total ? (n / j.total) * 100 : 0}%`;
  return (
    <div className="flex h-2 w-40 overflow-hidden rounded bg-gray-100" title={`${j.done} done · ${j.skipped} skipped · ${j.failed} failed · ${j.pending} pending`}>
      <div className="bg-green-500" style={{ width: pct(j.done) }} />
      <div className="bg-gray-400" style={{ width: pct(j.skipped) }} />
      <div className="bg-red-500" style={{ width: pct(j.failed) }} />
    </div>
  );
};

/** Asset beat entries: the scheduled tasks that keep media in sync. */
const ASSET_ENTRY = /asset|media|privacy|reconcile|phash|uploadcare/i;

const Scheduled = () => {
  const [env, setEnv] = useState<'production' | 'staging'>('production');
  const { data, isLoading, isError, error } = useWorkersScheduleQuery(env);
  const run = useWorkerChangeMutation();
  const { hasPermission: canOperate, isSuperAdmin } = useHasPermission('workers.operate');
  const entries = (data?.entries ?? []).filter((e) => ASSET_ENTRY.test(`${e.name} ${e.task ?? ''}`));
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm">
        {(['production', 'staging'] as const).map((e) => (
          <button key={e} onClick={() => setEnv(e)} className={cn('rounded-full border px-3 py-1', env === e ? 'border-gray-900 bg-gray-900 text-white' : 'bg-white')}>{e}</button>
        ))}
        <span className="text-xs text-gray-500">From the Workers console. Staging has no scheduler of its own.</span>
      </div>
      {isLoading ? <Skeleton className="h-40" /> : isError ? (
        <p className="text-sm text-red-600">{getApiErrorMessage(error, 'Could not load the schedule (needs workers.view)')}</p>
      ) : (
        <table className="w-full rounded-lg border bg-white text-sm">
          <thead className="bg-gray-50 text-left text-xs text-gray-500">
            <tr><th className="px-3 py-2">Entry</th><th className="px-3 py-2">Every</th><th className="px-3 py-2">Last run</th><th className="px-3 py-2">Next</th><th className="px-3 py-2">Last 24h</th><th /></tr>
          </thead>
          <tbody className="divide-y">
            {entries.map((e) => (
              <tr key={e.name}>
                <td className="px-3 py-2"><div className="font-medium">{e.name}</div><div className="text-xs text-gray-500">{e.task}</div></td>
                <td className="px-3 py-2">{e.every_sec ? `${Math.round(e.every_sec / 60)} min` : e.schedule.kind}</td>
                <td className="px-3 py-2">{e.last_run_at ? new Date(e.last_run_at * 1000).toLocaleString() : '—'}</td>
                <td className="px-3 py-2">{e.due_at ? new Date(e.due_at * 1000).toLocaleString() : '—'}</td>
                <td className="px-3 py-2 text-xs">{e.day ? `${e.day.runs} runs · ${e.day.failed} failed` : '—'}</td>
                <td className="px-3 py-2 text-right">
                  {(canOperate || isSuperAdmin) && (
                    <Button size="sm" variant="outline" disabled={run.isPending} onClick={async () => {
                      try {
                        await run.mutateAsync({ env, reason: 'Run now from Asset jobs', change: { kind: 'run_entry', entry: e.name } });
                        toast.success(`${e.name} queued on ${env}`);
                      } catch (err) { toast.error(getApiErrorMessage(err, 'Could not run it')); }
                    }}><Play className="mr-1 h-3.5 w-3.5" /> Run now</Button>
                  )}
                </td>
              </tr>
            ))}
            {!entries.length && <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">No asset entries in this schedule.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
};

const JobDetail = ({ ajid, onClose }: { ajid: number | null; onClose: () => void }) => {
  const { data: job } = useJobQuery(ajid);
  const [itemStatus, setItemStatus] = useState('failed');
  const { data: items } = useJobItemsQuery(ajid, itemStatus);
  const control = useJobControlMutation();
  const [reason, setReason] = useState('');

  const act = async (verb: 'cancel' | 'retry-failed' | 'revert') => {
    try {
      const out = await control.mutateAsync({ ajid: ajid!, verb, reason: verb === 'revert' ? reason.trim() : undefined });
      toast.success(verb === 'revert' ? `Revert started as job #${out.ajid}` : verb === 'cancel' ? 'Cancelled' : 'Retrying failed items');
      setReason('');
    } catch (e) { toast.error(getApiErrorMessage(e, 'Could not change the job')); }
  };

  const csv = () => {
    const rows = [['asid', 'status', 'result'], ...(items?.items ?? []).map((i) => [i.asid, i.status, i.result ?? ''])];
    const blob = new Blob([rows.map((r) => r.join(',')).join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `asset-job-${ajid}-${itemStatus || 'all'}.csv`;
    a.click();
  };

  const revertible = job && job.kind === 'bulk' && !job.reverted_by && !['queued', 'running'].includes(job.status) && job.done > 0;
  return (
    <Sheet open={ajid != null} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-[420px] overflow-y-auto sm:w-[560px]">
        <SheetHeader>
          <SheetTitle>Job #{ajid}</SheetTitle>
          <SheetDescription>{job ? describe(job) : ''}</SheetDescription>
        </SheetHeader>
        {!job ? <Skeleton className="mt-4 h-40" /> : (
          <div className="mt-4 space-y-4 text-sm">
            <div className="flex items-center gap-2">
              <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', STATUS_CLS[job.status])}>{job.status}</span>
              <Progress j={job} />
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
              <div>Total {job.total}</div><div>Done {job.done}</div><div>Skipped {job.skipped}</div><div>Failed {job.failed}</div>
              <div>Pending {job.pending}</div><div>Paid calls {job.paid_calls}</div>
              <div>Started {when(job.started_at)}</div><div>Finished {when(job.finished_at)}</div>
              <div className="col-span-2">Reason: {job.reason}</div>
              {job.env && <div>From {job.env}</div>}
              {job.reverted_by && <div className="col-span-2 text-amber-700">Reverted by job #{job.reverted_by}</div>}
              {job.error && <div className="col-span-2 text-red-700">{job.error}</div>}
              <div className="col-span-2 break-all">Scope: {JSON.stringify(job.scope)}</div>
            </div>
            <div className="flex flex-wrap gap-2">
              {['queued', 'running'].includes(job.status) && (
                <Button size="sm" variant="outline" onClick={() => void act('cancel')}><Ban className="mr-1 h-3.5 w-3.5" /> Cancel</Button>
              )}
              {!['queued', 'running'].includes(job.status) && job.failed > 0 && (
                <Button size="sm" variant="outline" onClick={() => void act('retry-failed')}><RotateCcw className="mr-1 h-3.5 w-3.5" /> Retry failed</Button>
              )}
            </div>
            {revertible && (
              <div className="space-y-2 rounded-md border p-3">
                <div className="font-medium">Revert</div>
                <p className="text-xs text-gray-600">Undoes every completed item as a new job (sellers are told when photos come back or go again).</p>
                <Input placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
                <Button size="sm" variant="outline" disabled={!reason.trim() || control.isPending} onClick={() => void act('revert')}>
                  <Undo2 className="mr-1 h-3.5 w-3.5" /> Revert {job.done}
                </Button>
              </div>
            )}
            <div>
              <div className="mb-2 flex items-center gap-2">
                <select aria-label="Item status" className="rounded-md border px-2 py-1 text-xs" value={itemStatus} onChange={(e) => setItemStatus(e.target.value)}>
                  {['failed', 'skipped', 'done', 'pending', ''].map((s) => <option key={s} value={s}>{s || 'all'}</option>)}
                </select>
                <span className="text-xs text-gray-500">{items?.total ?? 0} items</span>
                <Button size="sm" variant="ghost" className="ml-auto" disabled={!items?.items.length} onClick={csv}><Download className="mr-1 h-3.5 w-3.5" /> CSV</Button>
              </div>
              <ul className="max-h-72 divide-y overflow-y-auto rounded border text-xs">
                {(items?.items ?? []).slice(0, 300).map((i) => (
                  <li key={i.asid} className="flex gap-2 px-2 py-1">
                    <a className="w-16 text-blue-600 hover:underline" href={`/admin/assets?q=${i.asid}`}>#{i.asid}</a>
                    <span className="w-16">{i.status}</span><span className="text-gray-500">{i.result}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

/** Asset jobs (was Bulk Operations): every background run over many photos. */
export const BulkOperationsPage = () => {
  useBreadcrumbs([{ label: 'Asset Management', href: '/admin/assets' }, { label: 'Jobs' }]);
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'runs';
  const openJob = params.get('job') ? Number(params.get('job')) : null;
  const setTab = (t: string) => setParams((p) => { const n = new URLSearchParams(p); n.set('tab', t); return n; });
  const setJob = (id: number | null) => setParams((p) => { const n = new URLSearchParams(p); id ? n.set('job', String(id)) : n.delete('job'); return n; });

  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, error, refetch, isFetching } = useJobsQuery(kind, status, page);

  const [paste, setPaste] = useState('');
  const pasted = useMemo(() => Array.from(new Set((paste.match(/\d+/g) ?? []).map(Number))), [paste]);
  const [launch, setLaunch] = useState(false);

  return (
    <div className="min-h-screen space-y-5 bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Jobs</h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-500">
            Bulk moderation, deletes and analysis runs over many photos, in the background. Start one from a library selection or
            filter, from the Signals page, or by pasting asset IDs below.
          </p>
        </div>
        <div className="flex gap-2 border-b">
          {[['runs', 'Runs'], ['new', 'New run'], ['scheduled', 'Scheduled']].map(([t, l]) => (
            <button key={t} onClick={() => setTab(t)} className={cn('-mb-px border-b-2 px-3 py-2 text-sm', tab === t ? 'border-gray-900 font-medium' : 'border-transparent text-gray-500')}>{l}</button>
          ))}
        </div>

        {tab === 'runs' && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <select aria-label="Kind" className="rounded-md border bg-white px-3 py-2 text-sm" value={kind} onChange={(e) => { setKind(e.target.value); setPage(1); }}>
                <option value="">All kinds</option><option value="bulk">Moderation / delete</option><option value="signal">Analysis</option><option value="revert">Reverts</option>
              </select>
              <select aria-label="Status" className="rounded-md border bg-white px-3 py-2 text-sm" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
                <option value="">Any status</option>
                {['queued', 'running', 'done', 'failed', 'cancelled'].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <Button variant="outline" size="sm" className="ml-auto" onClick={() => refetch()} disabled={isFetching}>
                <RefreshCw className={cn('mr-2 h-4 w-4', isFetching && 'animate-spin')} /> Refresh
              </Button>
            </div>
            {isLoading ? <Skeleton className="h-60" /> : isError ? (
              <p className="text-sm text-red-600">{getApiErrorMessage(error, 'Could not load jobs')}</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs text-gray-500">
                    <tr><th className="px-3 py-2">#</th><th className="px-3 py-2">What</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Progress</th><th className="px-3 py-2 text-right">Photos</th><th className="px-3 py-2">Reason</th><th className="px-3 py-2">Started</th></tr>
                  </thead>
                  <tbody className="divide-y">
                    {(data?.jobs ?? []).map((j) => (
                      <tr key={j.ajid} className="cursor-pointer hover:bg-gray-50" onClick={() => setJob(j.ajid)}>
                        <td className="px-3 py-2 text-gray-500">{j.ajid}</td>
                        <td className="px-3 py-2">{describe(j)}{j.reverted_by ? <span className="ml-1 text-xs text-amber-700">reverted</span> : null}</td>
                        <td className="px-3 py-2"><span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', STATUS_CLS[j.status])}>{j.status}</span></td>
                        <td className="px-3 py-2"><Progress j={j} /></td>
                        <td className="px-3 py-2 text-right tabular-nums">{j.done}/{j.total}{j.failed ? <span className="text-red-600"> · {j.failed} failed</span> : ''}</td>
                        <td className="max-w-[240px] truncate px-3 py-2 text-gray-600" title={j.reason}>{j.reason}</td>
                        <td className="px-3 py-2 text-xs text-gray-500">{when(j.date_added)}</td>
                      </tr>
                    ))}
                    {!data?.jobs.length && <tr><td colSpan={7} className="px-3 py-10 text-center text-gray-500">No jobs yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
            <div className="rounded-lg border bg-white px-4 py-3">
              <Pagination total={data?.total ?? 0} page={page} itemsPerPage={25} limit={25} setPage={setPage} showButtons />
            </div>
          </div>
        )}

        {tab === 'new' && (
          <div className="max-w-2xl space-y-3 rounded-lg border bg-white p-4">
            <p className="text-sm text-gray-600">Paste asset IDs (any separator). For a filter, use the library: filter, then "Run a job on all matching".</p>
            <textarea className="h-32 w-full rounded-md border p-2 font-mono text-xs" value={paste} onChange={(e) => setPaste(e.target.value)} placeholder="52637, 52636 52635…" />
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">{pasted.length} IDs</span>
              <Button disabled={!pasted.length} onClick={() => setLaunch(true)}>Choose an action…</Button>
            </div>
          </div>
        )}

        {tab === 'scheduled' && <Scheduled />}
      </div>

      <JobDetail ajid={openJob} onClose={() => setJob(null)} />
      <JobLauncherDialog open={launch} onClose={() => setLaunch(false)} scope={{ asids: pasted }} scopeLabel={`${pasted.length} pasted IDs`} />
    </div>
  );
};
